import { gql } from '@apollo/client';

import { DraftReturn } from '@rebound/shared-models/draft-return/draft-return.model';

export const DraftReturnFragment = gql`
  fragment DraftReturnFields on DraftReturn {
    _id
    orderReference
    orderId
    step
    currentStepSlug
    currencyCode
    name
    email
    phoneNumber
    country
    county
    postCode
    city
    state
    address1
    address2
    companyName
    carrierServiceId
    carrierServiceName
    carrierServiceType
    postalCompanyId
    carrierServiceImgPath
    cycleonReturnOrderId
    cycleonRmaNumber
    isCarrierServiceFree
    deliveryPrice
    deliveryCurrency
    deliveryWasPaid
    paymentMethod
    paymentDetails
    voucher
    collectionDate
    collectionInterval
    dropOffPointId
    dropOffPointAddress
    returnWasCreated
    downloadReturnsFormUrl
    label
    labelType
    tracking
    feedbackRate
    feedbackNote
    language
    items {
      _id
      skuCode
      skuDesc
      cycleonId
      exportAwb
      exportCarrierName
      imgPath
      quantity
      price
      action
      clientMetadata
      reasons {
        action
        reason
        reasonCategory
        wish
        comment
        code
        itemToExchange {
          itemId
          skuCode
          skuDesc
          imgPath
          color {
            label
            hex
          }
          size
        }
        storeCreditsToExchange {
          value
          unit
        }
      }
    }
  }
`;

export const GetDraftReturnQuery = gql`
  query getDraftReturn($draftReturnId: String!) {
    getDraftReturn(draftReturnId: $draftReturnId) {
      ...DraftReturnFields
    }
  }

  ${DraftReturnFragment}
`;

export type GetDraftReturnQueryResult = {
  getDraftReturn: DraftReturn;
};

export const GetDraftReturnByRoIdQuery = gql`
  query getDraftReturnByRoId($returnOrderId: String!) {
    getDraftReturnByRoId(returnOrderId: $returnOrderId) {
      ...DraftReturnFields
    }
  }

  ${DraftReturnFragment}
`;

export type GetDraftReturnByRoIdQueryResult = {
  getDraftReturnByRoId: DraftReturn;
};

export const GetUnfinishedDraftReturnQuery = gql`
  query getUnfinishedDraftReturn($orderReference: String!) {
    getUnfinishedDraftReturn(orderReference: $orderReference) {
      ...DraftReturnFields
    }
  }

  ${DraftReturnFragment}
`;

export type GetUnfinishedDraftReturnQueryResult = {
  getUnfinishedDraftReturn: DraftReturn;
};

export const CreateDraftReturnMutation = gql`
  mutation createDraftReturn($orderId: String!, $identifier: String!) {
    createDraftReturn(orderId: $orderId, identifier: $identifier) {
      ...DraftReturnFields
    }
  }

  ${DraftReturnFragment}
`;

export type CreateDraftReturnMutationResult = {
  createDraftReturn: DraftReturn;
};

export const UpdateDraftReturnMutation = gql`
  mutation updateDraftReturn(
    $data: UpdateDraftReturnInput!
    $retailerSubdomain: String
  ) {
    updateDraftReturn(data: $data, retailerSubdomain: $retailerSubdomain) {
      ...DraftReturnFields
    }
  }

  ${DraftReturnFragment}
`;

export type UpdateDraftReturnMutationResult = {
  updateDraftReturn: DraftReturn;
};

export const DeleteDraftReturnMutation = gql`
  mutation deleteDraftReturn(
    $draftReturnId: String!
    $retailerSubdomain: String!
    $skipHeadlessApiCall: Boolean
  ) {
    deleteDraftReturn(
      draftReturnId: $draftReturnId
      retailerSubdomain: $retailerSubdomain
      skipHeadlessApiCall: $skipHeadlessApiCall
    ) {
      ...DraftReturnFields
    }
  }

  ${DraftReturnFragment}
`;

export type DeleteDraftReturnMutationResult = {
  deleteDraftReturn: DraftReturn;
};

export const CreateDraftReturnItemMutation = gql`
  mutation createDraftReturnItem(
    $draftReturnId: String!
    $data: CreateDraftReturnItemInput!
  ) {
    createDraftReturnItem(draftReturnId: $draftReturnId, data: $data) {
      ...DraftReturnFields
    }
  }

  ${DraftReturnFragment}
`;

export type CreateDraftReturnItemMutationResult = {
  createDraftReturnItem: DraftReturn;
};

export const UpdateDraftReturnItemMutation = gql`
  mutation updateDraftReturnItem(
    $draftReturnId: String!
    $data: UpdateDraftReturnItemInput!
  ) {
    updateDraftReturnItem(draftReturnId: $draftReturnId, data: $data) {
      ...DraftReturnFields
    }
  }

  ${DraftReturnFragment}
`;

export type UpdateDraftReturnItemMutationResult = {
  updateDraftReturnItem: DraftReturn;
};

export const DeleteDraftReturnItemMutation = gql`
  mutation deleteDraftReturnItem($draftReturnId: String!, $itemId: String!) {
    deleteDraftReturnItem(draftReturnId: $draftReturnId, itemId: $itemId) {
      ...DraftReturnFields
    }
  }

  ${DraftReturnFragment}
`;

export type DeleteDraftReturnItemMutationResult = {
  deleteDraftReturnItem: DraftReturn;
};

export const CreateDraftReturnWithoutSalesOrderMutation = gql`
  mutation createDraftReturnWithoutSalesOrder(
    $input: CreateDraftReturnWithoutSOInput!
  ) {
    createDraftReturnWithoutSalesOrder(input: $input) {
      ...DraftReturnFields
    }
  }

  ${DraftReturnFragment}
`;

export type CreateDraftReturnWithoutSalesOrderMutationResult = {
  createDraftReturnWithoutSalesOrder: DraftReturn;
};
